import React from "react";
import styled, { keyframes } from "styled-components";
import { useI18n } from "./i18n";

const fadeUp = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;

const Card = styled.article`background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};border-radius:12px;padding:1.25rem;cursor:pointer;transition:border-color .2s,transform .2s,box-shadow .2s;animation:${fadeUp} .4s ease both;animation-delay:${({$idx})=>$idx*.05}s;display:flex;flex-direction:column;gap:1rem;&:hover{border-color:${({theme})=>theme.borderLight};transform:translateY(-2px);box-shadow:0 8px 32px rgba(0,0,0,.4);}`;
const Header = styled.div`display:flex;gap:.75rem;align-items:flex-start;`;
const Thumb  = styled.div`width:44px;height:44px;border-radius:8px;background:${({theme})=>theme.border};flex-shrink:0;overflow:hidden;display:flex;align-items:center;justify-content:center;font-size:1.3rem;img{width:100%;height:100%;object-fit:cover;}`;
const Meta   = styled.div`flex:1;`;
const Category = styled.div`font-family:${({theme})=>theme.fontMono};font-size:.65rem;letter-spacing:.08em;text-transform:uppercase;color:${({theme})=>theme.textMuted};margin-bottom:.25rem;`;
const Title  = styled.h3`font-family:${({theme})=>theme.fontBody};font-size:.88rem;font-weight:600;line-height:1.35;color:${({theme})=>theme.text};`;
const StatusBadge = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.6rem;padding:.2rem .5rem;border-radius:4px;letter-spacing:.06em;font-weight:500;background:${({$s,theme})=>$s==="OPEN"?theme.greenDim:$s==="RESOLVED"?"#1a1a2e":"#1a1a10"};color:${({$s,theme})=>$s==="OPEN"?theme.green:$s==="RESOLVED"?theme.accent:theme.gold};border:1px solid ${({$s,theme})=>$s==="OPEN"?theme.green+"44":$s==="RESOLVED"?theme.accent+"44":theme.gold+"44"};`;
const Outcomes = styled.div`display:flex;flex-direction:column;gap:.5rem;`;
const OutcomeRow = styled.div`display:flex;align-items:center;gap:.6rem;`;
const OutcomeName= styled.span`font-size:.75rem;color:${({theme})=>theme.textDim};width:48px;flex-shrink:0;`;
const BarTrack = styled.div`flex:1;height:5px;background:${({theme})=>theme.border};border-radius:3px;overflow:hidden;`;
const BarFill  = styled.div`height:100%;width:${({$pct})=>$pct}%;background:${({$g,theme})=>$g?theme.green:theme.red};border-radius:3px;transition:width .5s ease;`;
const Pct = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.75rem;font-weight:500;color:${({$g,theme})=>$g?theme.green:theme.red};min-width:36px;text-align:right;`;
const Footer = styled.div`display:flex;justify-content:space-between;align-items:center;padding-top:.5rem;border-top:1px solid ${({theme})=>theme.border};`;
const Vol    = styled.div`font-family:${({theme})=>theme.fontMono};font-size:.68rem;color:${({theme})=>theme.textMuted};span{color:${({theme})=>theme.textDim};}`;
const EVPBadge = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.58rem;font-weight:700;color:${({theme})=>theme.green};background:${({theme})=>theme.greenDim};border:1px solid ${({theme})=>theme.green}33;border-radius:3px;padding:.05rem .3rem;margin-left:.2rem;`;
const TradeBtn = styled.button`font-family:${({theme})=>theme.fontBody};font-size:.72rem;font-weight:600;padding:.3rem .8rem;border-radius:6px;border:1px solid ${({theme})=>theme.green}55;background:${({theme})=>theme.greenDim};color:${({theme})=>theme.green};cursor:pointer;transition:all .15s;letter-spacing:.04em;&:hover{background:${({theme})=>theme.green}33;border-color:${({theme})=>theme.green};}`;

const EMOJI_MAP = { Politics:"🏛️", Politique:"🏛️", Sports:"⚽", Crypto:"₿", Finance:"📈", Tech:"💻", Science:"🔬", Entertainment:"🎬", Other:"🌐" };

const STATUS_LABELS = {
  OPEN:     { fr:"OUVERT",  en:"OPEN",    es:"ABIERTO",   de:"OFFEN",      pt:"ABERTO",   ar:"مفتوح"  },
  CLOSED:   { fr:"FERMÉ",   en:"CLOSED",  es:"CERRADO",   de:"GESCHLOSSEN",pt:"FECHADO",  ar:"مغلق"   },
  RESOLVED: { fr:"RÉSOLU",  en:"RESOLVED",es:"RESUELTO",  de:"AUFGELÖST",  pt:"RESOLVIDO",ar:"محسوم"  },
};

const T = {
  vol:   { fr:"Vol", en:"Vol", es:"Vol", de:"Vol", pt:"Vol", ar:"حجم" },
  trade: { fr:"Trader →", en:"Trade →", es:"Operar →", de:"Handeln →", pt:"Negociar →", ar:"تداول →" },
};

export default function MarketCard({ market, idx, onSelect, onTrade }) {
  const { lang } = useI18n();
  const outcomes = market.outcomes || [];
  const emoji    = EMOJI_MAP[market.category_name] || "🎯";
  const vol      = parseFloat(market.volume || 0);
  const volStr   = vol >= 1_000_000 ? `${(vol/1_000_000).toFixed(1)}M`
                 : vol >= 1_000     ? `${(vol/1_000).toFixed(1)}K`
                 : vol.toFixed(0);
  const API = process.env.REACT_APP_API_URL || "";
  const statusLabel = STATUS_LABELS[market.status]?.[lang] || market.status;

  return (
    <Card $idx={idx} onClick={() => onSelect(market)}>
      <Header>
        <Thumb>
          {market.image_url ? <img src={API + market.image_url} alt={market.title}/> : emoji}
        </Thumb>
        <Meta>
          <Category>{market.category_name}</Category>
          <Title>{market.title}</Title>
        </Meta>
        <StatusBadge $s={market.status}>{statusLabel}</StatusBadge>
      </Header>

      <Outcomes>
        {outcomes.slice(0, 4).map((o, i) => {
          const pct = Math.round(parseFloat(o.probability) * 100);
          return (
            <OutcomeRow key={o.id}>
              <OutcomeName>{o.name}</OutcomeName>
              <BarTrack><BarFill $pct={pct} $g={i === 0}/></BarTrack>
              <Pct $g={i === 0}>{pct}%</Pct>
            </OutcomeRow>
          );
        })}
      </Outcomes>

      <Footer>
        <Vol>{T.vol[lang] || T.vol.fr} <span>{volStr}<EVPBadge>EVP</EVPBadge></span></Vol>
        {market.status === "OPEN" && (
          <TradeBtn onClick={e => { e.stopPropagation(); onSelect(market); }}>
            {T.trade[lang] || T.trade.fr}
          </TradeBtn>
        )}
      </Footer>
    </Card>
  );
}

//onClick={e => { e.stopPropagation(); onTrade(market); }}
